import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { Input } from '~/libs/ui/atoms/Input';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { useClickOutside } from '~/libs/utils/hooks';

import { useAvailabilitySearchOptions } from '../hooks/useAvailabilitySearchOptions';
import { buildSlugFromLabel } from '../utils/availabilitySlug';
import { DisabledActionTooltip } from './DisabledActionTooltip';
import { FilterField } from './FilterField';

export interface SearchAutocompleteItem {
	id: string;
	label: string;
	slug?: string;
}

export interface SearchAutocompleteProps {
	selectedItems: SearchAutocompleteItem[];
	disabled?: boolean;
	disabledTooltip?: string;
	onChange(items: SearchAutocompleteItem[]): void;
}

export interface SelectedSearchChipsProps {
	items: SearchAutocompleteItem[];
	onRemove(id: string): void;
}

export function SelectedSearchChips({ items, onRemove }: SelectedSearchChipsProps) {
	if (items.length === 0) {
		return null;
	}

	return (
		<div className='flex flex-wrap gap-2'>
			{items.map((item) => (
				<span
					key={item.id}
					className='inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded bg-cBlue-100 text-cBlue-800 text-sm'
				>
					{item.label}
					<button
						type='button'
						className='text-cBlue-600 hover:text-cBlue-900'
						aria-label={`Odebrat ${item.label}`}
						onClick={() => onRemove(item.id)}
					>
						<FaTimes className='w-3 h-3' />
					</button>
				</span>
			))}
		</div>
	);
}

export function SearchAutocomplete({ selectedItems, disabled, disabledTooltip, onChange }: SearchAutocompleteProps) {
	const [filter, setFilter] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [inputResetKey, setInputResetKey] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const selectedIds = new Set(selectedItems.map((item) => item.id));
	const trimmedFilter = filter.trim();

	const { items, totalCount, isFetching, isError, isFilterTooShort } = useAvailabilitySearchOptions(
		trimmedFilter,
		isOpen,
		Boolean(disabled)
	);

	useClickOutside(containerRef, () => setIsOpen(false));

	useEffect(() => {
		if (disabled) {
			setIsOpen(false);
			setFilter('');
		}
	}, [disabled]);

	useEffect(() => {
		const input = inputRef.current;
		if (!input || disabled) {
			return;
		}

		const handleFocus = () => setIsOpen(true);

		input.addEventListener('focus', handleFocus);
		return () => input.removeEventListener('focus', handleFocus);
	}, [disabled, inputResetKey]);

	const handleSelect = (item: SearchAutocompleteItem) => {
		if (selectedIds.has(item.id)) {
			onChange(selectedItems.filter((selected) => selected.id !== item.id));
			return;
		}

		onChange([...selectedItems, { ...item, slug: item.slug ?? buildSlugFromLabel(item.label) }]);
	};

	useEffect(() => {
		if (inputResetKey === 0) {
			return;
		}
		inputRef.current?.focus();
		inputRef.current?.setSelectionRange(0, 0);
	}, [inputResetKey]);

	const showDropdown = !disabled && isOpen;

	return (
		<div className='relative' ref={containerRef}>
			<FilterField label='Hledat ATC, účinnou látku nebo název LP' htmlFor='availability-search'>
				<div className='relative'>
					<DisabledActionTooltip disabled={Boolean(disabled)} tooltip={disabledTooltip} wrapperClassName='block w-full'>
						<div className='w-full'>
							<Input
								id='availability-search'
								key={inputResetKey}
								ref={inputRef}
								placeholder='Zadejte hledaný výraz…'
								value={filter}
								disabled={disabled}
								onBlur={() => {}}
								onChange={(value) => {
									if (disabled) {
										return;
									}
									setFilter(value);
									setIsOpen(true);
								}}
							/>
						</div>
					</DisabledActionTooltip>
					{showDropdown && (
						<div className='absolute top-full left-0 z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg'>
							{isFilterTooShort && <div className='px-3 py-2 text-sm text-gray-500'>Zadejte alespoň 2 znaky…</div>}

							{!isFilterTooShort && isFetching && (
								<div className='flex justify-center py-3'>
									<SpinningLoadingIcon className='text-gray-400' />
								</div>
							)}

							{!isFilterTooShort && !isFetching && isError && (
								<div className='px-3 py-2 text-sm text-red-600'>Data se nepodařilo načíst.</div>
							)}

							{!isFilterTooShort && !isFetching && !isError && items.length === 0 && (
								<div className='px-3 py-2 text-sm text-gray-500'>Nic nenalezeno</div>
							)}

							{!isFilterTooShort &&
								!isFetching &&
								!isError &&
								items.map((item) => {
									const isSelected = selectedIds.has(item.id);
									return (
										<button
											key={item.id}
											type='button'
											className={`flex w-full items-start gap-2 text-left px-3 py-2 text-sm hover:bg-cBlue-50 ${
												isSelected ? 'bg-cBlue-50 text-cBlue-700' : 'text-gray-900'
											}`}
											onClick={() => handleSelect(item)}
										>
											<span className='min-w-0 flex-1 whitespace-normal break-words'>{item.label}</span>
											{isSelected ? (
												<span className='flex-shrink-0 text-xs font-semibold text-cBlue-600'>Vybráno</span>
											) : null}
										</button>
									);
								})}

							{!isFilterTooShort && !isFetching && !isError && totalCount > items.length && (
								<p className='px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50'>
									Zobrazeno {items.length} z {totalCount}. Pro upřesnění zadejte hledaný výraz.
								</p>
							)}
						</div>
					)}
				</div>
			</FilterField>
		</div>
	);
}
